import { Box, Typography } from "@mui/material";
import { Fragment, useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";

const GitHub = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("github");
  const [installUrl, setInstallUrl] = useState("");
  const [allowlistIps, setAllowlistIps] = useState([]);
  const { getUrlGithubInstall, getPlatformStaticIPs } = useAPI();
  const colors = useColors();
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  useEffect(() => {
    getUrlGithubInstall().then((response) => {
      setInstallUrl(response.data.url);
    });
  }, []);

  useEffect(() => {
    getPlatformStaticIPs()
      .then((response) => setAllowlistIps(response.data))
      .catch((error) => console.error(error));
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Guideline
        title={
          !isIntegrated && !isIntegratedLoading
            ? "Once integrated, the Breeze platform will automatically read the configurations from GitHub and prioritize the suggested remediation."
            : ""
        }
        image={logos.GitHubLogo}
        name={"GitHub"}
        content={
          isIntegrated && !guideMode ? (
            <Fragment>
              <Typography variant={"h4"}>
                GitHub is already integrated!
              </Typography>
            </Fragment>
          ) : (
            <>
              <Box
                sx={{
                  marginBottom: 2,
                }}
              >
                {guideMode && <IntegrationGuideAlert />}
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep title={"Login to GitHub"}>
                  <GuidelineInstruction text="Go to GitHub and log in to your account." />
                  <GuidelineInstruction text="Ensure you are signed into GitHub as an Enterprise or Organization Owner." />
                </GuidelineStep>
                <GuidelineStep title="Add Breeze to the IP allowlist - Optional">
                  <Typography variant="subtitle1">
                    If your organization is not using an IP allowlist, skip to
                    step 3.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Go to your GitHub organization settings:{" "}
                    <strong>
                      Settings > Authentication security > IP allow list
                    </strong>
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                  >
                    IP Addresses:
                  </Typography>
                  {allowlistIps.map((ip, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        margin: 0,
                        padding: "4px 0",
                        marginLeft: "20px",
                      }}
                    >
                      <Typography variant="body1">{ip}</Typography>
                      <CustomCopyButton
                        copyTitle={"IP Address"}
                        copyText={ip}
                      />
                    </Box>
                  ))}

                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Add each IP address to your organization's allowlist to
                    ensure Breeze can access your GitHub resources.
                  </Typography>
                  <Typography variant="body1">
                    For detailed instructions, refer to{" "}
                    <strong>
                      <a
                        href="https://docs.github.com/en/organizations/keeping-your-organization-secure/managing-security-settings-for-your-organization/managing-allowed-ip-addresses-for-your-organization"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: colors.primary_main }}
                      >
                        GitHub's IP allowlist documentation
                      </a>
                    </strong>
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title={"Installation Steps"}>
                  <Typography
                    variant={"subtitle1"}
                    display={"flex"}
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"baseline"}
                  >
                    {"Visit the "}
                    <SecondaryButton
                      sx={{
                        "&:hover": {
                          background: colors.primary_8percent,

                          borderColor: colors.primary_main,
                          color: colors.primary_main,
                        },
                      }}
                      onClick={() =>
                        window.open(installUrl, "_blank", "noopener noreferrer")
                      }
                    >
                      {" installation page "}
                    </SecondaryButton>
                    {" for our app."}
                  </Typography>
                  <GuidelineInstruction text="Select the organization you wish to install the app on" />
                  <GuidelineInstruction text="Follow the on-screen instructions to complete the installation." />
                </GuidelineStep>
              </GuidelineStepByStep>
            </>
          )
        }
        subTitle={undefined}
        subContent={undefined}
      />
    </Box>
  );
};

export default GitHub;
